import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

import Button from '../../../components/ui/Button';

const RevenueChart = ({ deals = [] }) => {
  const [viewType, setViewType] = useState('monthly');
  const [chartType, setChartType] = useState('bar');
  const [monthlyData, setMonthlyData] = useState([]);
  const [quarterlyData, setQuarterlyData] = useState([]);
  const [summaryMetrics, setSummaryMetrics] = useState({
    actualRevenue: 0,
    forecast: 0,
    target: 0
  });

  useEffect(() => {
    if (!deals || deals?.length === 0) {
      setMonthlyData([]);
      setQuarterlyData([]);
      setSummaryMetrics({ actualRevenue: 0, forecast: 0, target: 0 });
      return;
    }

    const currentYear = new Date()?.getFullYear();
    const currentMonth = new Date()?.getMonth();

    // Calculate monthly data
    const monthlyStats = {};
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Initialize all months
    monthNames?.forEach((month, index) => {
      monthlyStats[month] = {
        period: month,
        actual: 0,
        forecast: 0,
        target: 0,
        monthIndex: index
      };
    });

    // Calculate actual revenue from won deals
    deals?.forEach(deal => {
      const dealDate = new Date(deal.created_at);
      const dealYear = dealDate?.getFullYear();
      const dealMonth = dealDate?.getMonth();
      const monthName = monthNames?.[dealMonth];
      
      if (dealYear === currentYear && monthlyStats?.[monthName]) {
        if (deal?.stage === 'won') {
          monthlyStats[monthName].actual += parseFloat(deal?.value) || 0;
        }
      }
    });

    // Calculate YTD actual revenue
    const ytdActual = Object.values(monthlyStats)?.filter(m => m?.monthIndex <= currentMonth)?.reduce((sum, m) => sum + m?.actual, 0);

    // Calculate average monthly revenue for forecasting
    const monthsWithData = Object.values(monthlyStats)?.filter(m => m?.monthIndex <= currentMonth && m?.actual > 0)?.length;
    const avgMonthlyRevenue = monthsWithData > 0 ? ytdActual / monthsWithData : 0;

    // Project forecast and target for remaining months
    Object.values(monthlyStats)?.forEach(month => {
      if (month?.monthIndex <= currentMonth) {
        // Past months: forecast = actual, target = 110% of actual
        month.forecast = month?.actual;
        month.target = month?.actual * 1.1;
      } else {
        // Future months: forecast based on average, target = 120% of forecast
        month.forecast = avgMonthlyRevenue * 1.05; // 5% growth
        month.target = month?.forecast * 1.15; // 15% stretch goal
        month.actual = 0;
      }
    });

    const monthlyArray = Object.values(monthlyStats);
    setMonthlyData(monthlyArray);

    // Calculate quarterly data
    const quarters = [
      { period: 'Q1', months: [0, 1, 2] },
      { period: 'Q2', months: [3, 4, 5] },
      { period: 'Q3', months: [6, 7, 8] },
      { period: 'Q4', months: [9, 10, 11] }
    ];

    const quarterlyArray = quarters?.map(q => {
      const quarterData = monthlyArray?.filter(m => q?.months?.includes(m?.monthIndex));
      return {
        period: q?.period,
        actual: quarterData?.reduce((sum, m) => sum + m?.actual, 0),
        forecast: quarterData?.reduce((sum, m) => sum + m?.forecast, 0),
        target: quarterData?.reduce((sum, m) => sum + m?.target, 0)
      };
    });

    setQuarterlyData(quarterlyArray);

    // Calculate summary metrics
    const fullYearForecast = monthlyArray?.reduce((sum, m) => sum + m?.forecast, 0);
    const fullYearTarget = monthlyArray?.reduce((sum, m) => sum + m?.target, 0);

    setSummaryMetrics({
      actualRevenue: ytdActual,
      forecast: fullYearForecast,
      target: fullYearTarget
    });
  }, [deals]);

  const currentData = viewType === 'monthly' ? monthlyData : quarterlyData;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-4 shadow-elevation-2">
          <p className="font-medium text-popover-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center justify-between space-x-4 mb-1">
              <div className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry?.color }}
                />
                <span className="text-sm text-muted-foreground capitalize">{entry?.dataKey}</span>
              </div>
              <span className="text-sm font-medium text-popover-foreground">
                ${entry?.value?.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const formatYAxis = (value) => {
    return `$${(value / 1000)?.toFixed(0)}K`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="bg-card border border-border rounded-xl p-6"
    >
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 space-y-4 lg:space-y-0">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Revenue Forecasting</h3>
          <p className="text-sm text-muted-foreground">Actual vs forecast vs target revenue</p>
        </div>
        
        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
          <div className="flex items-center space-x-2">
            <Button
              variant={viewType === 'monthly' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewType('monthly')}
            >
              Monthly
            </Button>
            <Button
              variant={viewType === 'quarterly' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewType('quarterly')}
            >
              Quarterly
            </Button>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant={chartType === 'bar' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setChartType('bar')}
              iconName="BarChart3"
              iconPosition="left"
            >
              Bar
            </Button>
            <Button
              variant={chartType === 'line' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setChartType('line')}
              iconName="TrendingUp"
              iconPosition="left"
            >
              Line
            </Button>
          </div>
        </div>
      </div>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'bar' ? (
            <BarChart data={currentData} barGap={10}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis 
                dataKey="period" 
                stroke="#64748B"
                fontSize={12}
              />
              <YAxis 
                stroke="#64748B"
                fontSize={12}
                tickFormatter={formatYAxis}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="actual" 
                fill="#10B981" 
                radius={[4, 4, 0, 0]}
                animationDuration={1000}
              />
              <Bar 
                dataKey="forecast" 
                fill="#8B5CF6" 
                radius={[4, 4, 0, 0]}
                animationDuration={1200}
              />
              <Bar 
                dataKey="target" 
                fill="#FCD34D" 
                radius={[4, 4, 0, 0]}
                animationDuration={1400}
              />
            </BarChart>
          ) : (
            <LineChart data={currentData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis 
                dataKey="period" 
                stroke="#64748B"
                fontSize={12}
              />
              <YAxis 
                stroke="#64748B"
                fontSize={12}
                tickFormatter={formatYAxis}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="actual"
                stroke="#10B981"
                strokeWidth={3}
                dot={{ fill: '#10B981', strokeWidth: 2, r: 6 }}
                animationDuration={1000}
              />
              <Line
                type="monotone"
                dataKey="forecast"
                stroke="#8B5CF6"
                strokeWidth={3}
                strokeDasharray="5 5"
                dot={{ fill: '#8B5CF6', strokeWidth: 2, r: 6 }}
                animationDuration={1200}
              />
              <Line
                type="monotone"
                dataKey="target"
                stroke="#FCD34D"
                strokeWidth={3}
                dot={{ fill: '#FCD34D', strokeWidth: 2, r: 6 }}
                animationDuration={1400}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6 pt-6 border-t border-border">
        <div className="flex items-center space-x-3 p-4 bg-muted rounded-lg">
          <div className="w-4 h-4 bg-success rounded-full" />
          <div>
            <p className="text-sm font-medium text-foreground">Actual Revenue</p>
            <p className="text-lg font-semibold text-foreground">${summaryMetrics?.actualRevenue?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
            <p className="text-xs text-muted-foreground">YTD performance</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3 p-4 bg-muted rounded-lg">
          <div className="w-4 h-4 bg-primary rounded-full" />
          <div>
            <p className="text-sm font-medium text-foreground">Forecast</p>
            <p className="text-lg font-semibold text-foreground">${summaryMetrics?.forecast?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
            <p className="text-xs text-muted-foreground">Full year projection</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3 p-4 bg-muted rounded-lg">
          <div className="w-4 h-4 bg-accent rounded-full" />
          <div>
            <p className="text-sm font-medium text-foreground">Target</p>
            <p className="text-lg font-semibold text-foreground">${summaryMetrics?.target?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
            <p className="text-xs text-muted-foreground">Annual goal</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default RevenueChart;